import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { StickyNote, Search, ClipboardCheck, Gift, FileDown, Hand, CalendarClock, FileText, Radio, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { API, authHeaders, errorText } from "@/lib/api";
import { useAsk } from "@/components/admin/AskDialog";
import { useCan } from "@/components/admin/Me";

export const CRM_STATUSES = ["New", "Contacted", "Qualified", "Call Scheduled", "Booked", "Not a fit"];
const STATUS_TONE = { New: "bg-slate-100 text-slate-700", Contacted: "bg-sky-50 text-sky-800", Qualified: "bg-blue-50 text-blue-800", "Call Scheduled": "bg-teal-50 text-teal-800", Booked: "bg-[#0B1B33] text-white", "Not a fit": "bg-slate-200 text-slate-600" };

const Ctx = createContext({ contacts: {}, update: async () => {} });
export const useContacts = () => useContext(Ctx);

export const ContactsProvider = ({ children }) => {
  const [contacts, setContacts] = useState({});
  const leads = useCan()("leads");
  useEffect(() => {
    if (!leads) return;
    axios.get(`${API}/admin/contacts`, { headers: authHeaders() })
      .then((r) => setContacts(r.data && typeof r.data === "object" ? r.data : {}))
      .catch((e) => toast.error(errorText(e)));
  }, [leads]);
  const update = useCallback(async (email, patch) => {
    try {
      const { data } = await axios.put(`${API}/admin/contacts/${encodeURIComponent(email)}`, patch, { headers: authHeaders() });
      setContacts((c) => ({ ...c, [email]: data }));
      toast.success(patch.status ? `Status set to ${patch.status}${["Contacted", "Call Scheduled"].includes(patch.status) ? " · email sent" : ""}` : patch.follow_up !== undefined ? "Follow-up date saved" : "Notes saved");
    } catch (e) { toast.error(errorText(e)); }
  }, []);
  return <Ctx.Provider value={{ contacts, update }}>{children}</Ctx.Provider>;
};

const NotesDialog = ({ email, name, open, onClose }) => {
  const { contacts, update } = useContacts();
  const [text, setText] = useState("");
  useEffect(() => { if (open) setText(contacts[email]?.notes || ""); }, [open, email, contacts]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="notes-dialog" className="max-w-lg">
        <DialogTitle className="font-display text-[20px] text-[#0B1B33]">Notes for {name}</DialogTitle>
        <DialogDescription>{email} · shared across every list</DialogDescription>
        <textarea data-testid="notes-textarea" value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} rows={6} placeholder="e.g. Called on Monday, wants weekend batch, follow up after payday"
          className="w-full rounded-lg border border-slate-300 p-3 text-[14px] outline-none focus:border-[#0E9384] focus:ring-2 focus:ring-teal-100" />
        <button data-testid="notes-save-btn" onClick={async () => { await update(email, { notes: text }); onClose(); }} className="btn-teal rounded-lg py-3 font-semibold">Save notes</button>
      </DialogContent>
    </Dialog>
  );
};

export const StatusSelect = ({ email }) => {
  const { contacts, update } = useContacts();
  const v = contacts[email]?.status || "New";
  return (
    <Select value={v} onValueChange={(s) => update(email, { status: s })}>
      <SelectTrigger data-testid={`crm-status-${email}`} className={`h-9 w-40 border-0 font-semibold ${STATUS_TONE[v]}`}><SelectValue /></SelectTrigger>
      <SelectContent>{CRM_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
    </Select>
  );
};

export const NotesButton = ({ email, name }) => {
  const { contacts } = useContacts();
  const [open, setOpen] = useState(false);
  const notes = contacts[email]?.notes;
  return (
    <>
      <button data-testid={`crm-notes-${email}`} onClick={() => setOpen(true)} className="flex max-w-[200px] items-start gap-1.5 rounded-md px-2 py-1.5 text-left text-[13px] text-slate-700 hover:bg-slate-100">
        <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#0E9384]" />
        <span className="line-clamp-2">{notes || <span className="text-slate-500">Add note</span>}</span>
      </button>
      <NotesDialog email={email} name={name} open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export const FollowUp = ({ email }) => {
  const { contacts, update } = useContacts();
  const v = contacts[email]?.follow_up || "";
  const due = v && v <= todayStr();
  return (
    <label className={`mt-1 flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] ${due ? "bg-[#0B1B33] text-white" : "text-slate-600"}`}>
      <CalendarClock className="h-3.5 w-3.5 shrink-0" />
      <input data-testid={`crm-followup-${email}`} type="date" value={v} onChange={(e) => update(email, { follow_up: e.target.value })}
        className="w-[120px] bg-transparent outline-none [color-scheme:light]" />
    </label>
  );
};

export const todayStr = () => new Date().toLocaleDateString("en-CA");

export const CrmCells = ({ email, name }) => (
  <>
    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><StatusSelect email={email} /></td>
    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><NotesButton email={email} name={name} /><FollowUp email={email} /></td>
  </>
);

const EV = { diagnostic: [ClipboardCheck, "bg-teal-50 text-[#0E9384]"], toolkit: [Gift, "bg-sky-50 text-sky-700"], brochure: [FileDown, "bg-blue-50 text-blue-700"], interest: [Hand, "bg-[#0B1B33] text-white"], resume: [FileText, "bg-amber-50 text-amber-800"],
  free: [Radio, "bg-violet-50 text-violet-700"] };

const Timeline = ({ p, onClose }) => (
  <Dialog open={Boolean(p)} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="person-timeline" className="max-h-[90vh] max-w-xl overflow-y-auto">
      {p && <>
        <DialogTitle className="font-display text-[22px] text-[#0B1B33]">{p.name}</DialogTitle>
        <DialogDescription>{p.email}{p.phone ? ` · ${p.phone}` : ""}</DialogDescription>
        <div className="flex flex-wrap items-center gap-3"><StatusSelect email={p.email} /><NotesButton email={p.email} name={p.name} /><FollowUp email={p.email} /></div>
        <ol className="relative mt-2 space-y-4 border-l-2 border-slate-200 pl-6">
          {p.events.map((e, i) => {
            const [I, c] = EV[e.type] || EV.diagnostic;
            return (
              <li key={i} data-testid={`timeline-event-${e.type}`} className="relative">
                <span className={`absolute -left-[37px] grid h-7 w-7 place-items-center rounded-full ${c}`}><I className="h-3.5 w-3.5" /></span>
                <p className="text-[12px] text-slate-500">{new Date(e.at).toLocaleString()}</p>
                <p className="font-semibold text-[#0B1B33]">{e.title}</p>
                {e.detail && <p className="text-[13px] text-slate-600">{e.detail}</p>}
              </li>
            );
          })}
        </ol>
      </>}
    </DialogContent>
  </Dialog>
);

export const PeopleTab = () => {
  const { contacts } = useContacts();
  const ask = useAsk();
  const can = useCan();
  const [view, setView] = useState("active");
  const [people, setPeople] = useState([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [picked, setPicked] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    const h = { headers: authHeaders() };
    axios.get(`${API}/admin/people?view=${view}`, h).then((r) => setPeople(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errorText(e)));
    axios.get(`${API}/admin/people?view=archived`, h).then((r) => setArchivedCount(Array.isArray(r.data) ? r.data.length : 0)).catch(() => {});
    setPicked([]);
  }, [view]);
  useEffect(() => { load(); }, [load]);
  const isDue = (p) => { const f = contacts[p.email]?.follow_up; return Boolean(f) && f <= todayStr(); };
  const shown = useMemo(() => people.filter((p) => (view === "archived" || status === "all" || (status === "due" ? isDue(p) : (contacts[p.email]?.status || "New") === status))
    && `${p.name} ${p.email} ${p.phone || ""}`.toLowerCase().includes(q.toLowerCase())), [people, q, status, contacts, view]); // eslint-disable-line react-hooks/exhaustive-deps
  const allPicked = shown.length > 0 && shown.every((p) => picked.includes(p.email));
  const toggle = (email) => setPicked((x) => (x.includes(email) ? x.filter((e) => e !== email) : [...x, email]));
  const post = async (path, emails, msg) => {
    try { await axios.post(`${API}/admin/${path}`, { emails }, { headers: authHeaders() }); toast.success(msg); load(); } catch (e) { toast.error(errorText(e)); }
  };
  const archive = async (emails) => {
    if (!(await ask({ title: `Archive ${emails.length} ${emails.length === 1 ? "person" : "people"}?`, confirmLabel: "Archive",
      description: "They disappear from every list, count and CSV export. Nothing is deleted, and you can restore them from the Archived view." }))) return;
    post("archive", emails, `Archived ${emails.length}`);
  };
  const remove = async (p) => {
    const typed = await ask({ title: "Delete permanently?", danger: true, confirmLabel: "Delete forever", input: { type: "email", placeholder: p.email },
      description: `This erases everything for ${p.name} (${p.email}): diagnostic answers, enquiries, toolkit, Resume Studio data, notes, and any student account and uploads. It can't be undone. Type the email to confirm.` });
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== p.email.toLowerCase()) return toast.error("The email didn't match. Nothing was deleted.");
    try {
      await axios.delete(`${API}/admin/people/${encodeURIComponent(p.email)}`, { params: { confirm: typed.trim() }, headers: authHeaders() });
      toast.success(`Deleted all data for ${p.email}`); load();
    } catch (e) { toast.error(errorText(e)); }
  };
  const box = "h-4 w-4 cursor-pointer accent-[#0E9384]";
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 rounded-xl bg-white p-1 ring-1 ring-slate-200">{[["active", "Active"], ["archived", `Archived (${archivedCount})`]].map(([k, l]) => (
          <button key={k} data-testid={`people-view-${k}`} onClick={() => setView(k)} className={`rounded-lg px-3 py-2 text-[13.5px] font-semibold ${view === k ? "bg-[#0B1B33] text-white" : "text-slate-700"}`}>{l}</button>))}</div>
        {picked.length > 0 && (view === "active"
          ? <button data-testid="people-archive-selected" onClick={() => archive(picked)} className="flex items-center gap-1.5 rounded-lg bg-[#0B1B33] px-4 py-2.5 text-[14px] font-semibold text-white"><Archive className="h-4 w-4" /> Archive {picked.length} selected</button>
          : <button data-testid="people-restore-selected" onClick={() => post("restore", picked, `Restored ${picked.length}`)} className="flex items-center gap-1.5 rounded-lg bg-[#0E9384] px-4 py-2.5 text-[14px] font-semibold text-white"><ArchiveRestore className="h-4 w-4" /> Restore {picked.length} selected</button>)}
      </div>
      {view === "active" ? (
      <div className="grid grid-cols-3 gap-3 md:grid-cols-7">
        <button data-testid="people-due-today" onClick={() => setStatus(status === "due" ? "all" : "due")} className={`rounded-xl p-3 text-left transition-colors ${status === "due" ? "bg-[#0E9384] text-white" : "bg-[#0B1B33] text-white"}`}>
          <p className="flex items-center gap-1 text-[12px] text-slate-200"><CalendarClock className="h-3.5 w-3.5" /> Due today</p>
          <p className="font-display text-[24px] font-bold">{people.filter(isDue).length}</p>
        </button>
        {CRM_STATUSES.map((s) => (
          <button key={s} data-testid={`people-stat-${s}`} onClick={() => setStatus(status === s ? "all" : s)} className={`rounded-xl border p-3 text-left transition-colors ${status === s ? "border-[#0E9384] bg-teal-50" : "border-slate-200 bg-white"}`}>
            <p className="text-[12px] text-slate-600">{s}</p>
            <p className="font-display text-[24px] font-bold text-[#0B1B33]">{people.filter((p) => (contacts[p.email]?.status || "New") === s).length}</p>
          </button>
        ))}
      </div>) : (
        <p data-testid="people-archived-note" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[13.5px] text-amber-900">Archived people are hidden from lists, counts and exports. <b>Restore</b> brings them back. <b>Delete permanently</b> erases all their data, for test entries or "delete my data" requests. Backups keep a copy for up to 14 days.</p>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-500" />
        <input data-testid="people-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or phone" className="w-full rounded-lg border border-slate-300 bg-white py-3 pl-9 pr-3 outline-none focus:border-[#0E9384]" />
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table data-testid="people-table" className="w-full min-w-[900px] text-left text-[14px]">
          <thead className="bg-slate-50 text-[12px] uppercase tracking-wider text-slate-600">
            <tr><th className="w-10 px-4 py-3"><input type="checkbox" data-testid="people-pick-all" aria-label="Select all shown" checked={allPicked} onChange={() => setPicked(allPicked ? [] : shown.map((p) => p.email))} className={box} /></th>
              {["Person", "Journey", "Last activity", ...(view === "active" ? ["Status", "Notes & follow-up", ""] : ["Archived", ""])].map((h, i) => <th key={i} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {shown.map((p) => (
              <tr key={p.email} data-testid="people-row" className={`cursor-pointer hover:bg-slate-50 ${picked.includes(p.email) ? "bg-teal-50/50" : ""}`} onClick={() => setOpen(p)}>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" data-testid={`people-pick-${p.email}`} aria-label={`Select ${p.email}`} checked={picked.includes(p.email)} onChange={() => toggle(p.email)} className={box} /></td>
                <td className="px-4 py-3"><p className="font-semibold text-[#0B1B33]">{p.name}</p><p className="text-slate-600">{p.email}{p.phone ? ` · ${p.phone}` : ""}</p></td>
                <td className="px-4 py-3"><div className="flex gap-1.5">{["toolkit", "diagnostic", "brochure", "interest", "free"].map((t) => {
                  const n = p.events.filter((e) => e.type === t).length;
                  const [I, c] = EV[t];
                  return <span key={t} title={t} className={`flex items-center gap-1 rounded-full px-2 py-1 text-[12px] font-semibold ${n ? c : "bg-slate-50 text-slate-300"}`}><I className="h-3.5 w-3.5" />{n || ""}</span>;
                })}</div></td>
                <td className="px-4 py-3 text-slate-700"><p className="text-[13px]">{p.events[0]?.title}</p><p className="text-[12px] text-slate-500">{new Date(p.last_at).toLocaleString()}</p></td>
                {view === "active" ? <>
                  <CrmCells email={p.email} name={p.name} />
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><button data-testid={`people-archive-${p.email}`} title="Archive" onClick={() => archive([p.email])} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><Archive className="h-4 w-4" /></button></td>
                </> : <>
                  <td className="px-4 py-3 text-[13px] text-slate-600">{p.archived_at ? new Date(p.archived_at).toLocaleDateString() : ""}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><div className="flex justify-end gap-2">
                    <button data-testid={`people-restore-${p.email}`} onClick={() => post("restore", [p.email], "Restored")} className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-[13px] font-semibold text-[#0B1B33]"><ArchiveRestore className="h-4 w-4" /> Restore</button>
                    {can("owner") && <button data-testid={`people-delete-${p.email}`} onClick={() => remove(p)} className="flex items-center gap-1 rounded-lg bg-red-50 px-3 py-1.5 text-[13px] font-semibold text-red-700 hover:bg-red-100"><Trash2 className="h-4 w-4" /> Delete permanently</button>}
                  </div></td>
                </>}
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-600">{view === "archived" ? "Nothing archived." : "No people yet."}</td></tr>}
          </tbody>
        </table>
      </div>
      <Timeline p={open} onClose={() => setOpen(null)} />
    </div>
  );
};
